import { useState } from "react";
import { CAMPO, Recado } from "./ui";
import {
  mensagemDoCatalogo,
  salvarProduto,
  type Catalogo,
  type ProdutoDoCatalogo,
} from "../lib/adminCatalogo";
import AdminCatalogoProdutoBonus from "./AdminCatalogoProdutoBonus";

/**
 * Admin › Catálogo › Produtos › o produto › **editar um SKU**.
 *
 * ---------------------------------------------------------------------------
 * Por que uma tela dedicada, e por que o formulário da LISTA saiu junto
 * ---------------------------------------------------------------------------
 * Pedido de Victor em 21/09: *"a tela de produtos deve ter uma tela de edição
 * dedicada assim como nas ferramentas, quando clicamos no botão Abrir"*.
 *
 * É o mesmo movimento que a ferramenta fez em 16/09, e a parte que importa é a
 * mesma: **o formulário que morava na lista SAIU no mesmo ato**. Duas telas
 * editando o mesmo produto seriam duas portas para a mesma ação, e a segunda
 * porta é a que fica desatualizada — está escrito no cabeçalho de
 * `AdminCatalogoFerramenta.tsx` e no de `AdminCatalogo.tsx`, e é a razão de não
 * existir aba "Acessos" neste painel.
 *
 * ---------------------------------------------------------------------------
 * A ficha é do CURSO; a edição é do SKU
 * ---------------------------------------------------------------------------
 * É a única diferença real em relação à ferramenta, e ela não é detalhe: um
 * card é um só, e a Escola é DOIS produtos (`escola-contabil` e
 * `escola-vitalicio`) com um curso na tela do aluno. Por isso a página do
 * produto continua sendo por curso — e quem se edita aqui é um SKU por vez,
 * escolhido por `?sku=<slug>`.
 *
 * O SKU vive na QUERY pelo mesmo motivo que a sub-aba é sub-rota: o operador
 * manda o link do que está editando a um colega, volta pelo botão do navegador,
 * e recarregar não o joga para a lista. Com a Escola, isso é a diferença entre
 * "editei o prazo da Escola" e "editei o prazo de QUAL das duas".
 *
 * ---------------------------------------------------------------------------
 * Esta tela não decide nada
 * ---------------------------------------------------------------------------
 * Quem valida o contrato, cobra o motivo, zera o que não pertence ao tipo de
 * prazo e conta quantos alunos perdem o curso da tela é `cat_admin_salvar_produto`.
 * Daqui sai o que foi digitado, e o que volta é a frase do banco, inteira.
 */

type Formulario = {
  slug: string;
  nome: string;
  cursoNome: string;
  prazoTipo: ProdutoDoCatalogo["prazo_tipo"];
  prazoMeses: string;
  prazoData: string;
  exigeContrato: boolean;
  contrato: string;
  memberkit: string;
  ativo: boolean;
  motivo: string;
};

function formularioDe(p: ProdutoDoCatalogo): Formulario {
  return {
    slug: p.slug,
    nome: p.nome,
    cursoNome: p.curso_nome,
    prazoTipo: p.prazo_tipo,
    prazoMeses: p.prazo_meses === null ? "" : String(p.prazo_meses),
    prazoData: p.prazo_data_fixa ?? "",
    exigeContrato: p.exige_contrato,
    // O campo mostra a URL inteira porque é o que o operador reconhece ao
    // comparar com a tela da ZapSign. Ele aceita a referência também — quem
    // adivinha qual dos dois veio é o banco.
    contrato: p.contrato_url ?? "",
    memberkit: p.mk_membership_level === null ? "" : String(p.mk_membership_level),
    ativo: p.ativo,
    // O motivo nunca vem preenchido: ele descreve ESTE ato, não o anterior.
    motivo: "",
  };
}

export default function AdminCatalogoProdutoEditar({
  catalogo,
  produto,
  recarregar,
  aoFechar,
}: {
  catalogo: Catalogo;
  produto: ProdutoDoCatalogo;
  recarregar: () => Promise<void>;
  /** Fecha a edição — na prática, tira o `?sku=` da query. */
  aoFechar: () => void;
}) {
  // O rascunho nasce do produto e é RECRIADO quando o slug muda: sem a chave, o
  // operador que trocasse de SKU na Escola veria os valores do anterior no
  // formulário do seguinte. Quem garante isso é o `key` de quem monta este
  // componente — e o estado inicial aqui só é lido uma vez, de propósito.
  const [form, setForm] = useState<Formulario>(() => formularioDe(produto));
  const [salvando, setSalvando] = useState(false);
  const [erroDoForm, setErroDoForm] = useState<string | null>(null);
  const [recado, setRecado] = useState<string | null>(null);

  async function salvar() {
    // O original vem do CATÁLOGO, não de uma cópia guardada ao abrir: assim a
    // comparação do contrato é sempre contra o que está no banco agora.
    const original = catalogo.produtos.find((p) => p.slug === form.slug);
    if (!original) {
      setErroDoForm("Este produto não está mais no catálogo que a tela carregou. Recarregue a aba.");
      return;
    }

    setSalvando(true);
    setErroDoForm(null);

    // O contrato é o ÚNICO campo que só vai quando MUDA, e a razão é o produto
    // em preparação: se o que está gravado é o placeholder (o caminho SEM
    // `/doc/`), reenviá-lo sem querer faz o banco recusar o salvamento inteiro
    // por `contrato_invalido` — e o operador que só queria corrigir o nome não
    // conseguiria salvar nada. Nulo não mexe, e é isso que o contrato dos nulos
    // da RPC existe para permitir.
    //
    // Os outros campos vão sempre: reenviar o mesmo valor neles é inócuo, e
    // string vazia LIMPA de propósito (é como o operador apaga um nível do
    // Memberkit que não existe mais).
    const contratoMudou = form.contrato.trim() !== (original.contrato_url ?? "");

    const r = await salvarProduto({
      slug: form.slug,
      nome: form.nome,
      curso_nome: form.cursoNome,
      prazo_tipo: form.prazoTipo,
      // Os campos do prazo que não são do tipo escolhido vão nulos: quem decide
      // o que fazer com eles é a RPC, que zera o que não pertence ao tipo.
      prazo_meses:
        form.prazoTipo === "meses" && form.prazoMeses.trim() !== ""
          ? Number(form.prazoMeses.trim())
          : null,
      prazo_data_fixa:
        form.prazoTipo === "data_fixa" && form.prazoData.trim() !== "" ? form.prazoData.trim() : null,
      exige_contrato: form.exigeContrato,
      contrato: contratoMudou ? form.contrato.trim() : null,
      // Zero LIMPA o nível do Memberkit — é o contrato da RPC, e não um palpite:
      // nulo ali quer dizer "não mexe", então "apagar" precisa de um valor.
      mk_membership_level: form.memberkit.trim() === "" ? 0 : Number(form.memberkit.trim()),
      // `forum_course` NÃO vai: o campo saiu da tela em 22/09 e nulo é "não
      // mexe" na RPC. Ver o comentário no lugar onde ele era desenhado.
      ativo: form.ativo,
      motivo: form.motivo,
    });
    setSalvando(false);

    if (!r.ok) {
      // A frase é a do BANCO, inteira. Ela é que carrega quantos alunos perdem o
      // curso da tela ao desativar — reescrevê-la aqui criaria uma segunda
      // tabela de frases, e a que divergisse seria a que ninguém lê.
      setErroDoForm(mensagemDoCatalogo(r));
      return;
    }
    // O `detalhe` do sucesso já traz os `aviso:` embutidos (prazo novo que só
    // vale para venda nova, contrato que não serve). Eles vêm juntos porque são
    // do mesmo ato: aplicado, com divergência conhecida.
    //
    // O recado fica NESTA tela, e a edição continua aberta: ao contrário da
    // lista antiga, aqui não há para onde "voltar" — quem salvou está na ficha
    // do produto e costuma salvar de novo (o motivo, esse sim, se apaga).
    setRecado(mensagemDoCatalogo(r));
    setForm((f) => ({ ...f, motivo: "" }));
    await recarregar();
  }

  // O estado do contrato GRAVADO, derivado UMA vez: a cor e a frase saem do
  // MESMO booleano. Duas leituras da mesma coisa é como a cor acaba dizendo
  // uma coisa e o texto outra.
  //
  // `contrato_serve` chega NULO quando não há link nenhum (o `like` de um nulo
  // é nulo), e por isso a leitura é por verdade, nunca por `=== false`. Para o
  // aluno os dois estados têm o mesmo desfecho — não há onde assinar —, então
  // tratá-los juntos é o certo.
  const produtoDoForm = catalogo.produtos.find((p) => p.slug === form.slug);
  const contratoServe = Boolean(produtoDoForm?.contrato_serve);
  const gravadoAgora = !produtoDoForm?.contrato_url
    ? "Gravado hoje: nada."
    : contratoServe
      ? "Gravado hoje: o link real da ZapSign."
      : "Gravado hoje: um placeholder — ele não abre documento para assinar.";
  // Quando isto preocupa depende do que se está PEDINDO, e a leitura é da
  // caixa de seleção (não do banco): marcar "exige contrato" num produto sem
  // link pinta a linha na hora, que é a resposta à pergunta que o operador
  // acabou de fazer. Sem exigência, só um link inútil gravado merece alerta.
  const contratoPreocupa = form.exigeContrato
    ? !contratoServe
    : Boolean(produtoDoForm?.contrato_url) && !contratoServe;

  return (
    <div className="flex flex-col gap-5">
    <section
      className="surface rise p-6 sm:p-7"
      role="group"
      aria-labelledby="titulo-form-produto"
      data-editando-sku={form.slug}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
        <h3 id="titulo-form-produto" className="font-display text-lg font-bold text-titulo">
          Editar {form.slug}
        </h3>
        {/* O ALCANCE, antes de qualquer campo — a mesma decisão da tela da
            ferramenta. Quem chegou aqui veio da página de um CURSO e não tem
            como saber sozinho que está mexendo em um SKU só: na Escola, o
            outro fica como estava. */}
        <p className="text-sm text-cinza" data-alcance-do-sku>
          {produto.alunos.toLocaleString("pt-BR")} aluno(s) com acesso vigente por este SKU
          {catalogo.produtos.filter((p) => p.curso_slug === produto.curso_slug).length > 1 &&
            " · o outro SKU deste curso não muda"}
        </p>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold text-tinta">Nome do produto</span>
          <input
            data-campo="nome"
            className={CAMPO}
            value={form.nome}
            onChange={(e) => setForm({ ...form, nome: e.target.value })}
          />
          <span className="text-xs text-cinza">O nome do SKU, como a venda chega.</span>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold text-tinta">Nome do curso</span>
          <input
            data-campo="curso_nome"
            className={CAMPO}
            value={form.cursoNome}
            onChange={(e) => setForm({ ...form, cursoNome: e.target.value })}
          />
          <span className="text-xs text-cinza">
            É o que o aluno lê na vitrine. Os SKUs do mesmo curso deveriam dizer o mesmo aqui.
          </span>
        </label>

        {/* ── O prazo ──────────────────────────────────────────────────── */}
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold text-tinta">Prazo</span>
          <select
            data-campo="prazo_tipo"
            className={CAMPO}
            value={form.prazoTipo}
            onChange={(e) =>
              setForm({ ...form, prazoTipo: e.target.value as Formulario["prazoTipo"] })
            }
          >
            <option value="meses">Meses a contar da compra</option>
            <option value="data_fixa">Até uma data fixa</option>
            <option value="vitalicio">Vitalício</option>
          </select>
          <span className="text-xs text-cinza">
            Vale para as vendas NOVAS: quem já comprou fica com o fim que a compra dele gravou.
          </span>
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          {/* Os dois campos existem SEMPRE, e o que não é do tipo escolhido
              fica desligado com a razão ao lado. Campo desabilitado sem
              explicação é o operador procurando o que ele fez de errado. */}
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta">Meses</span>
            <input
              data-campo="prazo_meses"
              className={CAMPO}
              inputMode="numeric"
              disabled={form.prazoTipo !== "meses"}
              value={form.prazoMeses}
              onChange={(e) =>
                setForm({ ...form, prazoMeses: e.target.value.replace(/\D/g, "").slice(0, 3) })
              }
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta">Data fixa</span>
            <input
              data-campo="prazo_data_fixa"
              className={CAMPO}
              type="date"
              disabled={form.prazoTipo !== "data_fixa"}
              value={form.prazoData}
              onChange={(e) => setForm({ ...form, prazoData: e.target.value })}
            />
          </label>

          {form.prazoTipo === "vitalicio" && (
            <span className="text-xs text-cinza sm:col-span-2">
              Vitalício não usa nenhum dos dois: o fim do acesso fica NULO, que é o que
              vitalício quer dizer aqui. Data grande não é vitalício — é dado sujo.
            </span>
          )}
        </div>

        {/* ── O contrato ───────────────────────────────────────────────── */}
        <label className="flex flex-col gap-1.5 sm:col-span-2">
          <span className="text-sm font-semibold text-tinta">Contrato da ZapSign</span>
          <input
            data-campo="contrato"
            className={CAMPO}
            value={form.contrato}
            onChange={(e) => setForm({ ...form, contrato: e.target.value })}
            placeholder="https://app.zapsign.com.br/verificar/doc/… ou só a referência"
          />
          <span className="text-xs text-cinza">
            Cole o link inteiro ou só a referência — o banco reconhece os dois. O link SEM o
            <code className="px-1">/doc/</code> é o placeholder, e ele não serve para assinar.
            Apagar o campo limpa o contrato, o que na prática desliga a exigência.
          </span>
          {/* O que está gravado HOJE, dito no lugar em que se decide o que
              colar. `contrato_serve` vem do banco: a primeira heurística
              desta casa, escrita no front, escondeu o botão "Assinar
              contrato" exatamente de quem ele servia. */}
          <span
            data-contrato-estado
            className={`text-xs ${contratoPreocupa ? "text-alerta-tinta" : "text-cinza"}`}
          >
            {gravadoAgora}
          </span>
        </label>

        <label className="flex min-h-12 items-center gap-2 text-sm text-tinta sm:col-span-2">
          <input
            type="checkbox"
            data-campo="exige_contrato"
            checked={form.exigeContrato}
            onChange={(e) => setForm({ ...form, exigeContrato: e.target.checked })}
            className="size-4 accent-acento"
          />
          Exige contrato assinado
          <span className="text-xs text-cinza">
            (sem assinatura o curso fica trancado na vitrine — o acesso continua)
          </span>
        </label>

        {/* ── As integrações ───────────────────────────────────────────── */}
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold text-tinta">Nível no Memberkit</span>
          <input
            data-campo="mk_membership_level"
            className={CAMPO}
            inputMode="numeric"
            value={form.memberkit}
            onChange={(e) =>
              setForm({ ...form, memberkit: e.target.value.replace(/\D/g, "").slice(0, 9) })
            }
          />
          <span className="text-xs text-cinza">
            É o que faz a porta "Aulas" abrir. Apagar o campo limpa o nível.
          </span>
        </label>

{/*
          O campo "Curso no Fórum" SAIU desta tela em 22/09, a pedido de Victor.

          A coluna `produtos.forum_course` continua existindo e continua valendo
          — os 12 produtos a têm preenchida, e é ela que faz o Fórum receber o
          produto. O que saiu foi a forma de editá-la aqui.

          Por isso `forum_course` também saiu do PAYLOAD, e não virou um campo
          escondido mandando o valor de volta: a RPC trata nulo como "não mexe"
          (`case when p_forum_course is null then forum_course`), então não
          mandar é o jeito de a tela não tocar no que não mostra. Mandar o valor
          lido de um campo invisível seria pior — bastaria a leitura mudar de
          forma para a tela reescrever a chave de 12 produtos sem ninguém ter
          pedido.
        */}

        <label className="flex min-h-12 items-center gap-2 text-sm text-tinta sm:col-span-2">
          <input
            type="checkbox"
            data-campo="ativo"
            checked={form.ativo}
            onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
            className="size-4 accent-acento"
          />
          Produto ativo
          <span className="text-xs text-cinza">
            (desativar tira o curso da tela de quem tem acesso vigente, e o banco cobra o porquê)
          </span>
        </label>

        <label className="flex flex-col gap-1.5 sm:col-span-2">
          <span className="text-sm font-semibold text-tinta">Justifique a alteração</span>
          <input
            data-campo="motivo"
            className={CAMPO}
            value={form.motivo}
            onChange={(e) => setForm({ ...form, motivo: e.target.value })}
            placeholder="ex.: prazo corrigido conforme a página de vendas"
          />
          <span className="text-xs text-cinza">
            Vai para o razão do catálogo, junto com o que mudou e quem mudou.
          </span>
        </label>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          data-salvar-produto
          onClick={() => void salvar()}
          disabled={salvando}
          className="pill-cta min-h-12 px-6 disabled:opacity-55"
        >
          {salvando ? "Salvando…" : "Salvar"}
        </button>
        <button type="button" onClick={aoFechar} data-fechar-edicao className="btn-contorno min-h-12">
          Fechar
        </button>
      </div>

      {/* A recusa fica AQUI, ao lado do campo de motivo que ela costuma
          pedir — e não no alto de uma página que já rolou. */}
      {erroDoForm && <Recado tipo="erro" texto={erroDoForm} />}
      {recado && <Recado tipo="ok" texto={recado} />}
    </section>

    {/* Os BÔNUS DE ACESSO (000273) ficam numa caixa PRÓPRIA, e não em mais um
        campo do formulário acima. São atos diferentes: aquele salva o cadastro
        do SKU de uma vez, e cada bônus entra ou sai sozinho, com o seu motivo e
        a sua linha no razão — juntá-los faria "salvar o nome" e "tirar um
        curso de bônus" virarem o mesmo clique. */}
    <AdminCatalogoProdutoBonus catalogo={catalogo} produto={produto} recarregar={recarregar} />
    </div>
  );
}
